import { Fragment } from "react";

import { Menu, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/outline";
import clsx from "clsx";
import _isNil from "lodash/isNil";
import _startCase from "lodash/startCase";

export const getLabel = item => (typeof item === "string" ? _startCase(item) : item?.label);
export const getValue = item => (typeof item === "string" ? item : item?.value);

type OptionType = { label: string; value: string | undefined } | string;

type DropdownItemProps = {
  selected: string | undefined;
  item: OptionType;
  labelFormatter?: (label: string) => string;
  onChange: (selected: string | undefined) => void;
};
const DropdownItem = ({ item, labelFormatter = v => v, onChange, selected }: DropdownItemProps) => {
  const label = labelFormatter(getLabel(item));
  const value = getValue(item);
  return (
    <Menu.Item key={value}>
      <button
        onClick={() => onChange(value)}
        className={clsx(
          "block px-4 py-1 w-full rounded-none text-left",
          value === undefined && "text-gray-400 italic",
          value !== undefined &&
            (value === selected ? "bg-gray-100 text-primary" : "text-gray-700 hover:bg-gray-50 hover:text-primary")
        )}
      >
        {label}
      </button>
    </Menu.Item>
  );
};

export type DropdownMenuProps = {
  allowClear?: boolean;
  className?: string;
  labelFormatter?: DropdownItemProps["labelFormatter"];
  options: OptionType[];
  placeholder?: string;
  selected: string | undefined;
  onChange: (selected: string | undefined) => void;
};
const DropdownMenu = ({
  allowClear,
  className = "flex-1 basis-full min-w-[100px] xs:basis-none",
  placeholder = "Select...",
  options,
  selected,
  onChange,
  labelFormatter = v => v,
}: DropdownMenuProps) => {
  if (!options?.length) {
    return null;
  }

  const selectedOption = !_isNil(selected) && options.find(o => selected === (typeof o === "string" ? o : o.value));

  return (
    <Menu as="div" className={clsx("relative inline-block", className)}>
      {({ open }) => (
        <>
          <Menu.Button
            className={clsx(
              selected ? "text-gray-700" : "text-gray-400",
              open ? "rounded-t-md rounded-b-none" : "rounded-md",
              "inline-flex w-full items-center justify-between border border-gray-200 bg-white px-4 py-2 text-sm font-medium shadow hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-gray-100"
            )}
          >
            <span>{selected ? labelFormatter(getLabel(selectedOption)) : placeholder}</span>
            <ChevronDownIcon className="h-4 w-4 ml-1" />
          </Menu.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute left-0 right-0 z-10 w-full min-w-[150px] max-h-60 overflow-auto py-2 origin-top-right bg-white rounded-b-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              {allowClear && (
                <DropdownItem
                  item={{ label: "Select option...", value: undefined }}
                  onChange={onChange}
                  selected={selected}
                />
              )}
              {options.map(i => (
                <DropdownItem
                  key={getValue(i)}
                  item={i}
                  labelFormatter={labelFormatter}
                  onChange={onChange}
                  selected={selected}
                />
              ))}
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  );
};

export default DropdownMenu;
